import { put, cancelled, select, call } from 'redux-saga/effects';
import { ActionType, createAction, createAsyncAction, createReducer } from 'typesafe-actions';
import { api } from 'api';
import { getArticle, getEnv, getRegionCode } from 'common/selectors';
import log from 'loglevel';
import type { CallEffectReturnType, EffectR } from 'types/utils';
import { YaPaySession } from 'types/yandex-pay';
import { getYandexSplitSession } from 'features/yandex-split-banner/selectors';
import { RESET_YANDEX_SPLIT_SESSION } from 'features/yandex-split-banner/reducer';
import { ProductMainInfo, ProductMainInfoProps } from '../types';
import { fetchProductMainInfo } from './api';

export const RESET = createAction('product/RESET')();
export const SET_PRODUCTID = createAction('product/SET_PRODUCTID')<string>();

export const FETCH_PRODUCT_MAIN_INFO = createAsyncAction(
  'product/FETCH_PRODUCT_MAIN_INFO/request',
  'product/FETCH_PRODUCT_MAIN_INFO/success',
  'product/FETCH_PRODUCT_MAIN_INFO/failure',
  'product/FETCH_PRODUCT_MAIN_INFO/cancel',
)<void, ProductMainInfo, Error, void>();

type MainInfoErrorActions =
  | ActionType<typeof FETCH_PRODUCT_MAIN_INFO.request>
  | ActionType<typeof FETCH_PRODUCT_MAIN_INFO.success>
  | ActionType<typeof FETCH_PRODUCT_MAIN_INFO.failure>
  | ActionType<typeof RESET>;
export const mainInfoError = createReducer<string | null, MainInfoErrorActions>(null)
  .handleAction([FETCH_PRODUCT_MAIN_INFO.failure], (_, { payload }) => payload.message)
  .handleAction([FETCH_PRODUCT_MAIN_INFO.request, FETCH_PRODUCT_MAIN_INFO.success, RESET], () => null);

export const mainInfoloading = createReducer(false)
  .handleAction([FETCH_PRODUCT_MAIN_INFO.request], () => true)
  .handleAction(
    [
      FETCH_PRODUCT_MAIN_INFO.success,
      FETCH_PRODUCT_MAIN_INFO.failure,
      FETCH_PRODUCT_MAIN_INFO.cancel,
      RESET,
    ],
    () => false,
  );

export const productMainInfo = createReducer<ProductMainInfoProps | null>(null)
  .handleAction(
    [
      // FETCH_PRODUCT_MAIN_INFO.request,
      FETCH_PRODUCT_MAIN_INFO.failure,
      FETCH_PRODUCT_MAIN_INFO.cancel,
      RESET,
    ],
    () => null,
  )
  .handleAction(FETCH_PRODUCT_MAIN_INFO.success, (_, { payload }) => payload?.content?.[0] ?? null);

export function* doFetchProductData() {
  const productId: string = yield select(getArticle);
  const regionCode: string = yield select(getRegionCode);
  const yandexSplitSession: YaPaySession = yield select(getYandexSplitSession);
  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;
  if (yandexSplitSession) {
    yield put(RESET_YANDEX_SPLIT_SESSION());
  }
  yield put(FETCH_PRODUCT_MAIN_INFO.request());
  const { requestID } = (yield select(getEnv)) as EffectR.Select<typeof getEnv>;
  yield call(log.info, 'Product-main-info was requested', 'requestID: ', requestID, 'dateNow: ', Date.now());

  try {
    const productMainInfoPath = 'get-product-main-info';
    const data = (yield call(
      fetchProductMainInfo,
      {
        productId,
        regionCode,
      },
      abortController.token,
      productMainInfoPath,
    )) as CallEffectReturnType<typeof fetchProductMainInfo>;

    yield put(FETCH_PRODUCT_MAIN_INFO.success(data));
    yield call(log.info, 'Product-main-info was loaded', 'requestID: ', requestID, 'dateNow: ', Date.now());
  } catch (e) {
    if (e instanceof Error) {
      yield put(FETCH_PRODUCT_MAIN_INFO.failure(e));
      yield call(
        log.info,
        'Product-main-info has error',
        'requestID: ',
        requestID,
        'dateNow: ',
        Date.now(),
        'e',
        e,
      );
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield call(
        log.info,
        'Product-main-info was cancelled',
        'requestID: ',
        requestID,
        'dateNow: ',
        Date.now(),
      );
      yield put(FETCH_PRODUCT_MAIN_INFO.cancel());
    }
  }
}
